import React, { useState, useEffect, useCallback } from 'react';
import { GoHash } from 'react-icons/go';
import { CiUser } from 'react-icons/ci';
import { FaRegFileAlt } from 'react-icons/fa';
import { MdOutlineFeaturedPlayList, MdOutlineWorkOutline, MdRefresh } from 'react-icons/md';
import { PiRepeatFill } from 'react-icons/pi';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { TbEdit, TbProgress } from 'react-icons/tb';
import { IoCheckmarkDoneCircleOutline, IoClose } from 'react-icons/io5';
import { LiaNotesMedicalSolid } from 'react-icons/lia';
import { BiReceipt } from 'react-icons/bi';
import ImageViewer from '../components/ui/ImageViewer';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import Button from '../components/ui/Button/Button';
import { RxDownload } from 'react-icons/rx';
import TableSearchFilter from '../components/utils/TableSearchFilter';
import ResponsiveTable from '../components/Table/ResponsiveTable';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { TableSkeleton } from '../components/Table/TableSkeleton';
import { useProjectData } from '../hooks/useProjectData';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useCustomerData } from '../hooks/useCustomerData';
import { useUserProjects } from '../hooks/useUserProjects';
import {
  FiPlus,
  FiFileText,
  FiClock,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import PopupWrapper from '../components/utils/PopupWrapper';
import { usePopup } from '../hooks/usePopup';
import InputField from '../components/ui/InputField';
import { RiFileTextLine } from 'react-icons/ri';
import { BsListCheck, BsCurrencyRupee } from 'react-icons/bs';
import ProjectFilterSlider from '../components/utils/ProjectFilterSlider';
import { fetchProjectsByStatus, loadProjects, fetchOriginProject, fetchReopenedProject } from '../redux/actions/projectAction';
import { clearCache, clearOriginProject, clearReopenedProject } from '../redux/slices/projectSlice';
import { fetchUserProjectsByStatus, fetchUserProjects } from '../redux/actions/userProjectsAction'; // Add import for user projects by status
import axiosInstance from '../axios';
import FilterPanel from '../components/utils/FilterPanel';
import TaskAssignmentTable from '../components/Projects/TaskAssignmentTable';
import { extractCustomerName, getCustomerName } from '../utils/customerUtils';
import { toast } from 'react-hot-toast';
import { hasProjectEditPermission } from '../utils/permissionUtils';
import { hasStatusPermission, normalizeStatus } from '../utils/permissionUtils';
import { store } from '../redux/store';

// New Skeleton component for table rows
const SkeletonRow = () => (
  <div className="animate-pulse flex justify-between items-center py-4 border-b border-gray-200">
    <div className="w-[5%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[20%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[10%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[25%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[10%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[15%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[10%] h-6 bg-gray-200 rounded-full"></div>
  </div>
);

// Skeleton table component
const SkeletonTable = ({ rows = 5 }) => (
  <div className="w-full bg-white rounded-lg shadow-sm">
    <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
      <div className="w-full flex gap-8">
        {/* Column headers skeleton */}
        <div className="w-[5%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[20%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[10%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[25%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[10%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[15%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[10%] h-5 bg-gray-200 rounded"></div>
      </div>
    </div>
    <div className="p-4">
      {/* Generate skeleton rows */}
      {Array(rows)
        .fill(0)
        .map((_, index) => (
          <SkeletonRow key={index} />
        ))}
    </div>
    {/* Pagination skeleton */}
    <div className="p-4 flex justify-between items-center border-t border-gray-200">
      <div className="w-[15%] h-8 bg-gray-200 rounded"></div>
      <div className="flex gap-2">
        {Array(4)
          .fill(0)
          .map((_, index) => (
            <div key={index} className="w-8 h-8 bg-gray-200 rounded"></div>
          ))}
      </div>
    </div>
  </div>
);

const MyProjects: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Replace local state management with Redux hook
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Use our custom hook to get user projects
  const {
    userProjects,
    filteredUserProjects, // Add filteredUserProjects
    pagination,
    isLoading: userProjectsLoading,
    error: userProjectsError,
    refreshProjects,
  } = useUserProjects(currentPage, itemsPerPage);

  // Get customers from Redux store with our custom hook to ensure data is loaded
  const { customers, isLoading: customersLoading } = useCustomerData();

  // Get user data and permissions from auth state
  const { user } = useAppSelector(state => state.auth);

  // Debug log for Redux state
  useEffect(() => {
    console.log('[MyProjects] Redux State:', {
      user: user?.id ? `${user.fullname} (${user.id})` : 'Not logged in',
      userProjects: userProjects?.length || 0,
      userProjectsLoading,
      userProjectsError,
    });
  }, [user, userProjects, userProjectsLoading, userProjectsError]);

  // Check if user has project permissions
  const [hasProjectPermission, setHasProjectPermission] = useState(false);

  // Add local state for display data after filtering
  const [loading, setLoading] = useState(false);
  const [displayData, setDisplayData] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Add a state to track initial data loading
  const [initialLoading, setInitialLoading] = useState(true);

  // Show loading state while user projects are loading
  useEffect(() => {
    setLoading(userProjectsLoading);

    // If user projects are not loading anymore and we have data, set initialLoading to false
    if (!userProjectsLoading && userProjects && userProjects.length > 0) {
      setInitialLoading(false);
    }
  }, [userProjectsLoading, userProjects]);

  // Initial data fetch when component mounts
  useEffect(() => {
    console.log('[MyProjects] Initial mount effect triggered');
    console.log('[MyProjects] User ID:', user?.id);
    console.log('[MyProjects] Current userProjects:', userProjects?.length || 0);
    console.log('[MyProjects] Loading state:', userProjectsLoading);
    console.log('[MyProjects] Error state:', userProjectsError);

    // Skip if we're coming from dashboard with a status filter - the dashboard filter effect will handle it
    if (location.state?.statusFilter && location.state?.fromDashboard) {
      console.log('[MyProjects] Skipping initial fetch - dashboard filter effect will handle data loading');
      return;
    }

    // Fetch initial data when component mounts and user is available
    if (user?.id && !userProjectsLoading) {
      console.log('[MyProjects] Fetching initial data for user:', user.id);
      fetchProjects(1, itemsPerPage, statusFilter, searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Show error if user projects have an error
  useEffect(() => {
    if (userProjectsError) {
      console.error('User projects error:', userProjectsError);
    }
  }, [userProjectsError]);

  // Add effect to trigger search when searchValue changes with debounce
  useEffect(() => {
    console.log(
      `[MyProjects] Search effect triggered - searchValue: "${searchValue}", initialLoading: ${initialLoading}, userProjectsLoading: ${userProjectsLoading}`
    );

    // Only trigger search if we already have initial data
    if (!initialLoading && !userProjectsLoading) {
      const timer = setTimeout(() => {
        console.log(`[MyProjects] Search value changed: "${searchValue}" - Making API call`);

        // Clear current data and show loading
        setDisplayData([]);
        setLoading(true);

        // Reset to page 1 and fetch data with current status filter and search value
        setCurrentPage(1);
        fetchProjects(1, itemsPerPage, statusFilter, searchValue);
      }, 600);
      return () => clearTimeout(timer);
    } else {
      console.log(`[MyProjects] Skipping search - still loading initial data`);
    }
  }, [searchValue]);

  // Handle status filter change
  const handleStatusFilterChange = (filter: string) => {
    console.log(`[MyProjects] Filter clicked: ${filter}`);

    // Immediately clear current data and show loading
    setDisplayData([]);
    setFilterLoading(true);

    // Update the status filter state
    setStatusFilter(filter);

    // Reset to first page when filtering
    setCurrentPage(1);

    // If the filter is "All", fetch all user projects
    if (filter === 'All') {
      console.log('[MyProjects] Loading all user projects');

      // Call fetchProjects with explicit filter override
      fetchProjects(1, itemsPerPage, 'All', searchValue)
        .then(() => {
          setFilterLoading(false);
        })
        .catch(() => {
          setFilterLoading(false);
        });
      return;
    }

    // Convert filter names to API format
    let apiFilter = filter;

    // Handle special cases for status names
    switch (filter) {
      case 'In Progress':
        apiFilter = 'IN_PROGRESS';
        break;
      case 'On Hold':
        apiFilter = 'HOLD';
        break;
      case 'Payment Received':
        apiFilter = 'PAYMENT_RECEIVED';
        break;
      case 'Cancelled':
        apiFilter = 'CANCELLED';
        break;
      default:
        // For other cases, use simple conversion
        apiFilter = filter.toUpperCase();
    }

    console.log(`[MyProjects] Converted status filter: ${filter} -> ${apiFilter}`);

    // For specific status filters, use fetchProjects with filter override for consistency
    fetchProjects(1, itemsPerPage, filter, searchValue)
      .then(() => {
        setFilterLoading(false);
      })
      .catch(() => {
        setFilterLoading(false);
      });
  };

  // Process data from Redux to display format and handle pagination
  const fetchProjects = async (
    page: number,
    perPage: number,
    overrideFilter?: string,
    searchTerm?: string
  ) => {
    // Prevent multiple simultaneous calls
    if (loading || filterLoading || userProjectsLoading) {
      console.log('[MyProjects] fetchProjects - Skipping call, already loading:', {
        loading,
        filterLoading,
        userProjectsLoading,
      });
      return;
    }

    // Reset loading state
    setLoading(true);

    try {
      // Use searchTerm if provided, otherwise use current searchValue
      const currentSearch = searchTerm !== undefined ? searchTerm : searchValue;

      console.log(
        '[MyProjects] fetchProjects - Making API call for page',
        page,
        'with limit',
        perPage,
        'filter override:',
        overrideFilter,
        'search:',
        currentSearch
      );

      // Check for authentication errors
      if (userProjectsError && userProjectsError.includes('Authentication')) {
        console.error('[MyProjects] Authentication error:', userProjectsError);
        toast.error('Authentication error. Please log in again.');
        navigate('/auth/login');
        return;
      }

      // Make API call for the specific page instead of using cached data
      if (user?.id) {
        // Use overrideFilter if provided, otherwise use current statusFilter
        const currentFilter = overrideFilter !== undefined ? overrideFilter : statusFilter;

        console.log(
          `[MyProjects] fetchProjects - Making API call for user ${user.id}, page ${page}, limit ${perPage}, statusFilter: ${currentFilter}`
        );

        let result;

        // Check if we have an active status filter (not 'All')
        if (currentFilter && currentFilter !== 'All') {
          // Convert UI status to API status with proper mapping
          let apiStatus = currentFilter;
          switch (currentFilter) {
            case 'In Progress':
              apiStatus = 'IN_PROGRESS';
              break;
            case 'On Hold':
              apiStatus = 'HOLD';
              break;
            case 'Payment Received':
              apiStatus = 'PAYMENT_RECEIVED';
              break;
            case 'Cancelled':
              apiStatus = 'CANCELLED';
              break;
            case 'Unpaid Reopen':
              apiStatus = 'REVISION';
              break;
            case 'Paid Reopen':
              apiStatus = 'REOPEN';
              break;
            default:
              // For other cases, use simple conversion
              apiStatus = currentFilter.toUpperCase().replace(/\s+/g, '_');
          }

          console.log(
            `[MyProjects] fetchProjects - Using status filter: ${currentFilter} -> ${apiStatus}`
          );

          // Use fetchUserProjectsByStatus for filtered data
          result = await dispatch(
            fetchUserProjectsByStatus({
              userId: user.id,
              status: apiStatus,
              page: page,
              limit: perPage,
              search: currentSearch || undefined,
            })
          ).unwrap();
        } else {
          // Use fetchUserProjects for all projects
          console.log(`[MyProjects] fetchProjects - Fetching all projects (no status filter)`);

          result = await dispatch(
            fetchUserProjects({
              userId: user.id,
              page: page,
              limit: perPage,
              search: currentSearch || undefined,
            })
          ).unwrap();
        }

        console.log('[MyProjects] fetchProjects - API result:', result);

        if (result && result.data && Array.isArray(result.data)) {
          // Format projects for display
          const formattedProjects = result.data.map((project, index) => {
            // Get the latest status
            const latestStatus = getLatestProjectStatus(project);

            // Get customer name using our utility function
            let customerName = project.customerName;

            if (project.ClientId && customers.length > 0) {
              const matchingCustomer = customers.find(c => c.id === project.ClientId);
              if (matchingCustomer) {
                customerName = matchingCustomer.companyName;
              }
            }

            if (!customerName || customerName.startsWith('Client #')) {
              customerName = extractCustomerName(project, customers);
            }

            return {
              id: project.manakProjectId ? `${project.manakProjectId}` : `Project-${index + 1}`,
              status: formatStatusText(latestStatus),
              customerName: customerName,
              articleNo: project.projectNumber || `NT-${3015 + index}`,
              projectName: project.projectName || `Project ${index + 1}`,
              createdOn: {
                date: project.createdAt
                  ? formatDateToDDMMYY(project.createdAt)
                  : formatDateToDDMMYY(new Date()),
                time: extractTimeFromDate(project.createdAt),
              },
              rawProject: project,
            };
          });

          console.log('[MyProjects] fetchProjects - Formatted projects:', formattedProjects.length);

          // Set display data directly from API (no client-side pagination needed)
          setDisplayData(formattedProjects);

          // Update pagination metadata from API response
          if (result.pagination) {
            // Handle both old and new pagination format
            const total =
              (result.pagination as any).total || (result.pagination as any).totalItems || 0;
            const pages =
              (result.pagination as any).totalPages ||
              (result.pagination as any).pages ||
              Math.ceil(total / perPage);
            setTotalItems(total);
            setTotalPages(pages);
            console.log('[MyProjects] fetchProjects - Pagination metadata:', result.pagination);
            console.log(
              '[MyProjects] fetchProjects - Set totalItems:',
              total,
              'totalPages:',
              pages
            );
          } else {
            setTotalItems(formattedProjects.length);
            setTotalPages(Math.ceil(formattedProjects.length / perPage));
          }

          setInitialLoading(false);
        } else {
          console.log('[MyProjects] fetchProjects - No valid data in API response');
          setDisplayData([]);
          setTotalItems(0);
          setInitialLoading(false);
        }
      } else {
        console.error('[MyProjects] fetchProjects - No user ID available');
        setDisplayData([]);
        setTotalItems(0);
        setInitialLoading(false);
      }
    } catch (error) {
      console.error('[MyProjects] fetchProjects - Error:', error);
      setDisplayData([]);
      setTotalItems(0);
      setInitialLoading(false);

      // Handle specific error cases
      if (error instanceof Error && error.message.includes('Authentication')) {
        toast.error('Authentication error. Please log in again.');
        navigate('/auth/login');
      }
    } finally {
      setLoading(false);
      setFilterLoading(false); // Also reset filter loading state
    }
  };

  // Add state for showing assignment history
  const [showAssignmentHistory, setShowAssignmentHistory] = useState(false);
  const [assignmentHistory, setAssignmentHistory] = useState([]);

  // Get employee and designations data from Redux
  const { employees, designations } = useAppSelector(state => state.users);
  const { originProject, originProjectLoading, originProjectError, reopenedProject, reopenedProjectLoading, reopenedProjectError } = useAppSelector(state => state.projects);

  // Helper function to format status text for display
  const formatStatusText = (status: string): string => {
    if (!status) return '';

    // Replace underscores with spaces
    let formattedStatus = status.replace(/_/g, ' ');

    // Convert to title case (capitalize first letter of each word)
    formattedStatus = formattedStatus
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    // Handle special cases
    switch (status.toUpperCase()) {
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'PAYMENT_RECEIVED':
        return 'Payment Received';
      case 'NON_ACTIVE':
        return 'Non Active';
      default:
        return formattedStatus;
    }
  };

  // Helper function to format assignment history for display
  const formatAssignmentHistory = (project: any, employeesList: any[], currentUserData: any) => {
    if (!project?.projectAssigned || project.projectAssigned.length === 0) {
      return [];
    }

    // Sort assignments by date (newest first)
    const sortedAssignments = [...project.projectAssigned].sort((a, b) => {
      const dateA = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
      const dateB = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
      return dateA - dateB;
    });

    // Return the raw assignments - let the TaskAssignmentTable component handle all formatting
    return sortedAssignments;
  };

  // Format origin project data similar to selectedProject
  const formattedOriginProject = React.useMemo(() => {
    if (!originProject) return null;

    // Get the customer name for the origin project
    // Use ClientId (from database) or customerId (if mapped)
    const customerId = originProject.customerId || originProject.ClientId;
    const originCustomerName = getCustomerName(customerId, customers);

    // Get most recent status
    let currentStatus = originProject.status || 'Unassigned';
    if (originProject.projectAssigned && originProject.projectAssigned.length > 0) {
      const sortedAssignments = [...originProject.projectAssigned].sort(
        (a, b) =>
          new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
          new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
      );
      if (sortedAssignments[0]?.status) {
        currentStatus = sortedAssignments[0].status;
      }
    }

    return {
      id: originProject.manakProjectId,
      customerName: originCustomerName,
      articleNo: originProject.projectNumber || originProject.articleNo || 'N/A',
      projectName: originProject.projectName,
      status: formatStatusText(currentStatus),
      displayStatus: formatStatusText(currentStatus),
      rawProject: originProject,
    };
  }, [originProject, customers]);

  // Format origin project assignment history
  const originAssignmentHistory = React.useMemo(() => {
    if (!originProject) return [];
    return formatAssignmentHistory(originProject, employees, user);
  }, [originProject, employees, user]);

  // Format reopened project data similar to selectedProject
  const formattedReopenedProject = React.useMemo(() => {
    if (!reopenedProject) return null;

    // Get the customer name for the reopened project
    // Use ClientId (from database) or customerId (if mapped)
    const customerId = reopenedProject.customerId || reopenedProject.ClientId;
    const reopenedCustomerName = getCustomerName(customerId, customers);

    // Get most recent status
    let currentStatus = reopenedProject.status || 'Unassigned';
    if (reopenedProject.projectAssigned && reopenedProject.projectAssigned.length > 0) {
      const sortedAssignments = [...reopenedProject.projectAssigned].sort(
        (a, b) =>
          new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
          new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
      );
      if (sortedAssignments[0]?.status) {
        currentStatus = sortedAssignments[0].status;
      }
    }

    return {
      id: reopenedProject.manakProjectId,
      customerName: reopenedCustomerName,
      articleNo: reopenedProject.projectNumber || reopenedProject.articleNo || 'N/A',
      projectName: reopenedProject.projectName,
      status: formatStatusText(currentStatus),
      displayStatus: formatStatusText(currentStatus),
      rawProject: reopenedProject,
    };
  }, [reopenedProject, customers]);

  // Format reopened project assignment history
  const reopenedAssignmentHistory = React.useMemo(() => {
    if (!reopenedProject) return [];
    return formatAssignmentHistory(reopenedProject, employees, user);
  }, [reopenedProject, employees, user]);

  // Add state to manage the visibility of the search UI
  const [showSearchUI, setShowSearchUI] = useState(false);

  // Add a dedicated loading state for filter changes
  const [filterLoading, setFilterLoading] = useState(false);

  // Add state for status filter
  const [statusFilter, setStatusFilter] = useState('All');

  // Check for status filter passed in from Dashboard
  useEffect(() => {
    // Check if we have a status filter in location state (coming from Dashboard)
    if (location.state?.statusFilter && location.state?.fromDashboard) {
      const dashboardStatusFilter = location.state.statusFilter;
      console.log('[MyProjects] Status filter received from Dashboard:', dashboardStatusFilter);

      // Update the status filter state so the ProjectFilterSlider shows the correct filter
      setStatusFilter(dashboardStatusFilter);

      // Apply the filter with a small delay to ensure components are updated properly
      setFilterLoading(true);
      setTimeout(() => {
        handleStatusFilterChange(dashboardStatusFilter);

        // Clear the location state to prevent reapplying the filter on subsequent renders
        navigate(location.pathname, { replace: true, state: {} });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Process data from Redux store when userProjects change
  useEffect(() => {
    console.log('[MyProjects] userProjects changed:', {
      projectsCount: userProjects?.length || 0,
      loading: userProjectsLoading,
      error: userProjectsError,
      pagination,
      statusFilter,
    });

    // If we're not loading, update the initial loading state
    if (!userProjectsLoading) {
      setInitialLoading(false);

      // If we have an error, show empty state
      if (userProjectsError) {
        console.log('[MyProjects] userProjects has error:', userProjectsError);
        setDisplayData([]);
        setTotalItems(0);
        setTotalPages(1);
      }
      // If we have empty data, show empty state
      else if (userProjects && userProjects.length === 0) {
        console.log('[MyProjects] userProjects is empty array');
        setDisplayData([]);
        setTotalItems(0);
        setTotalPages(1);
      }
      // If we have data and statusFilter is 'All', format and display the data
      else if (
        userProjects &&
        userProjects.length > 0 &&
        statusFilter === 'All' &&
        displayData.length === 0
      ) {
        console.log('[MyProjects] Processing initial userProjects data for "All" status');
        applyFilterAndSetDisplayData(userProjects, 'All', currentPage, itemsPerPage);
      }
    }
  }, [userProjects, userProjectsLoading, userProjectsError, statusFilter, displayData.length]);

  // Add filter state for our FilterPanel
  const [filterValues, setFilterValues] = useState({
    customerName: '',
    articleNo: '',
    status: 'All',
    createdFrom: '',
    createdTo: '',
  });

  // Add debounce for filter inputs - MOVED THIS UP BEFORE applyFilters
  const [debouncedFilters, setDebouncedFilters] = useState(filterValues);

  // Handle filter changes from FilterPanel
  const handleFilterChange = (name: string, value: string) => {
    setFilterValues(prev => ({
      ...prev,
      [name]: value,
    }));

    // Update debounced filters immediately for now
    // In a production app, you might want to add actual debounce logic here
    setDebouncedFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Function to apply filters
  const applyFilters = useCallback(async () => {
    if (!showFilterPanel) return;

    setFilterLoading(true);

    try {
      // Check if any filter is applied
      const isFilterActive = Object.values(debouncedFilters).some(
        value => value && value !== 'All'
      );

      if (!isFilterActive) {
        // No filters applied, use data from Redux
        fetchProjects(currentPage, itemsPerPage, statusFilter, searchValue);
        setFilterLoading(false);
        return;
      }

      // If we can filter the data from user projects
      if (userProjects && Array.isArray(userProjects) && userProjects.length > 0) {
        let filteredData = [...userProjects];

        // Apply filters one by one
        if (debouncedFilters.customerName) {
          const customerNameLower = debouncedFilters.customerName.toLowerCase();
          filteredData = filteredData.filter(project => {
            let customerName = '';
            if (project.ClientId && customers.length > 0) {
              const matchingCustomer = customers.find(c => c.id === project.ClientId);
              if (matchingCustomer) {
                customerName = matchingCustomer.companyName.toLowerCase();
              }
            }
            return customerName.includes(customerNameLower);
          });
        }

        if (debouncedFilters.articleNo) {
          const articleNoLower = debouncedFilters.articleNo.toLowerCase();
          filteredData = filteredData.filter(project =>
            (project.projectNumber || '').toLowerCase().includes(articleNoLower)
          );
        }

        if (debouncedFilters.status && debouncedFilters.status !== 'All') {
          const statusLower = debouncedFilters.status.toLowerCase();
          filteredData = filteredData.filter(project => {
            const projectStatus = getLatestProjectStatus(project).toLowerCase();
            return projectStatus.includes(statusLower);
          });
        }

        if (debouncedFilters.createdFrom) {
          const fromDate = new Date(debouncedFilters.createdFrom);
          filteredData = filteredData.filter(project => {
            const createdDate = new Date(project.createdAt || Date.now());
            return createdDate >= fromDate;
          });
        }

        if (debouncedFilters.createdTo) {
          const toDate = new Date(debouncedFilters.createdTo);
          // Set time to end of day
          toDate.setHours(23, 59, 59, 999);
          filteredData = filteredData.filter(project => {
            const createdDate = new Date(project.createdAt || Date.now());
            return createdDate <= toDate;
          });
        }

        // Format filtered data for display
        const formattedProjects = filteredData.map((project, index) => {
          // Get the latest status
          const latestStatus = getLatestProjectStatus(project);

          // Get customer name - improved approach
          let customerName = project.customerName;

          // Always look up customer from Redux store to ensure we have the latest data
          if (project.ClientId && customers.length > 0) {
            const matchingCustomer = customers.find(c => c.id === project.ClientId);
            if (matchingCustomer) {
              customerName = matchingCustomer.companyName;
            }
          }

          // Ensure we don't show "Client #ID" fallback
          if (!customerName || customerName.startsWith('Client #')) {
            customerName = extractCustomerName(project, customers);
          }

          return {
            // Always use manakProjectId if available to ensure consistency on refresh
            id: project.manakProjectId ? `${project.manakProjectId}` : `Project-${index + 1}`,
            status: formatStatusText(latestStatus),
            customerName: customerName,
            articleNo: project.projectNumber || `NT-${3015 + index}`,
            projectName: project.projectName || `Project ${index + 1}`,
            createdOn: {
              date: project.createdAt
                ? formatDateToDDMMYY(project.createdAt)
                : formatDateToDDMMYY(new Date()),
              time: extractTimeFromDate(project.createdAt),
            },
            rawProject: project,
          };
        });

        // Apply pagination manually
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedData = formattedProjects.slice(startIndex, endIndex);

        setDisplayData(paginatedData);
        setTotalItems(formattedProjects.length);
        setFilterLoading(false);
        return;
      }

      // If we can't find data, make an API call
      console.log('Making API call for filtered data');

      // Prepare query parameters
      const queryParams = new URLSearchParams();

      if (debouncedFilters.status && debouncedFilters.status !== 'All') {
        queryParams.append('status', debouncedFilters.status.replace(' ', '_').toUpperCase());
      }

      if (debouncedFilters.customerName) {
        queryParams.append('customerName', debouncedFilters.customerName);
      }

      if (debouncedFilters.articleNo) {
        queryParams.append('projectNumber', debouncedFilters.articleNo);
      }

      if (debouncedFilters.createdFrom) {
        queryParams.append('createdFrom', debouncedFilters.createdFrom);
      }

      if (debouncedFilters.createdTo) {
        queryParams.append('createdTo', debouncedFilters.createdTo);
      }

      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', itemsPerPage.toString());

      // Make API call with all filters
      const response = await axiosInstance.get(
        `/v1/admin/project/assigned?${queryParams.toString()}`
      );

      if (response.data && response.data.success && response.data.data) {
        const apiProjects = response.data.data;

        // Format API response for display
        const formattedProjects = apiProjects.map((project: any, index: number) => {
          const status =
            project.projectAssigned && project.projectAssigned.length > 0
              ? project.projectAssigned[0].status
              : project.status || '';

          // Get customer name - improved approach
          let customerName = project.customerName;

          // Always look up customer from Redux store to ensure we have the latest data
          if (project.ClientId && customers.length > 0) {
            const matchingCustomer = customers.find(c => c.id === project.ClientId);
            if (matchingCustomer) {
              customerName = matchingCustomer.companyName;
            }
          }

          // Ensure we don't show "Client #ID" fallback
          if (!customerName || customerName.startsWith('Client #')) {
            customerName = extractCustomerName(project, customers);
          }

          return {
            // Always use manakProjectId if available to ensure consistency on refresh
            id: project.manakProjectId ? `${project.manakProjectId}` : `Project-${index + 1}`,
            status: formatStatusText(status),
            customerName: customerName,
            articleNo: project.projectNumber || `NT-${3015 + index}`,
            projectName: project.projectName || `Project ${index + 1}`,
            createdOn: {
              date: project.createdAt
                ? formatDateToDDMMYY(project.createdAt)
                : formatDateToDDMMYY(new Date()),
              time: extractTimeFromDate(project.createdAt),
            },
            rawProject: project,
          };
        });

        setDisplayData(formattedProjects);
        setTotalItems(response.data.pagination?.total || formattedProjects.length);
      } else {
        // No data returned from API
        setDisplayData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error applying filters:', error);
      setDisplayData([]);
      setTotalItems(0);
    } finally {
      setFilterLoading(false);
    }
  }, [debouncedFilters, currentPage, itemsPerPage, userProjects, customers, showFilterPanel]);

  // Helper function to format dates in DD-MM-YY format
  const formatDateToDDMMYY = (dateInput: string | Date): string => {
    const date = new Date(dateInput);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(2);
    return `${day}-${month}-${year}`;
  };

  // Helper function to extract time in HH:MM AM/PM format from date string
  const extractTimeFromDate = (dateInput: string | Date | null | undefined): string => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Helper function to highlight text matches
  const highlightMatch = (text: string | number, query: string) => {
    if (!query.trim() || !text) return text;

    const textStr = String(text);
    const regex = new RegExp(`(${query.trim()})`, 'gi');
    const parts = textStr.split(regex);

    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span key={i} className="bg-yellow-200 font-medium">
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  // Helper function to get the latest status from a project's assignments
  const getLatestProjectStatus = (project: any): string => {
    if (!project.projectAssigned || project.projectAssigned.length === 0) {
      return project.status || '';
    }

    // Sort assignments by date (newest first)
    const sortedAssignments = [...project.projectAssigned].sort((a, b) => {
      const dateA = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
      const dateB = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
      return dateA - dateB;
    });

    // Return the status of the most recent assignment
    return sortedAssignments[0].status || '';
  };

  // Function to handle viewing origin project
  const handleViewOriginProject = async (originProject: any) => {
    console.log('handleViewOriginProject - Origin project data:', originProject);

    if (!originProject?.id) {
      toast.error('Origin project ID not found');
      return;
    }

    try {
      // Fetch origin project using Redux action
      await dispatch(fetchOriginProject(originProject.id)).unwrap();

      // After successful fetch, trigger the slide animation
      setViewingOriginProject(true);
    } catch (error: any) {
      console.error('Error fetching origin project:', error);
      toast.error(error || 'Failed to fetch origin project');
    }
  };

  // Function to close origin project view
  const handleCloseOriginProject = () => {
    setViewingOriginProject(false);
    dispatch(clearOriginProject());
  };

  // Function to view reopened project (project created from current)
  const handleViewReopenedProject = async (reopenedProj: any) => {
    console.log('handleViewReopenedProject - Reopened project data:', reopenedProj);

    if (!reopenedProj?.id) {
      toast.error('Reopened project ID not found');
      return;
    }

    try {
      // Fetch reopened project using Redux action
      await dispatch(fetchReopenedProject(reopenedProj.id)).unwrap();

      // After successful fetch, trigger the slide animation
      setViewingReopenedProject(true);
    } catch (error: any) {
      console.error('Error fetching reopened project:', error);
      toast.error(error || 'Failed to fetch reopened project');
    }
  };

  // Function to close reopened project view
  const handleCloseReopenedProject = () => {
    setViewingReopenedProject(false);
    dispatch(clearReopenedProject());
  };

  // Helper function to process and apply filter to projects data
  const applyFilterAndSetDisplayData = (
    projects: any[],
    filter: string,
    page: number,
    perPage: number
  ) => {
    try {
      // Use the projects data passed directly to this function
      let projectsToDisplay = [...projects];

      // Apply search filter if search value exists - enhanced dynamic search
      if (searchValue.trim()) {
        const searchLower = searchValue.toLowerCase();
        projectsToDisplay = projectsToDisplay.filter(
          project =>
            // Search in multiple fields
            project.projectName?.toLowerCase().includes(searchLower) ||
            project.projectNumber?.toLowerCase().includes(searchLower) ||
            project.projectDescription?.toLowerCase().includes(searchLower) ||
            (project.manakProjectId &&
              project.manakProjectId.toString().toLowerCase().includes(searchLower)) ||
            // Search in status - use the latest status
            getLatestProjectStatus(project).toLowerCase().includes(searchLower) ||
            // Try to match customer name if possible
            (project.ClientId &&
              customers
                .find(c => c.id === project.ClientId)
                ?.companyName.toLowerCase()
                .includes(searchLower))
        );
      }

      // Apply status filter if not set to 'All'
      if (filter !== 'All') {
        projectsToDisplay = projectsToDisplay.filter(project => {
          // Get the latest status from the project
          const latestStatus = getLatestProjectStatus(project);

          // Normalize the status strings for comparison
          const normalizedProjectStatus = formatStatusText(latestStatus);
          const normalizedFilterStatus = filter === 'On Hold' ? 'Hold' : filter;

          return normalizedProjectStatus === normalizedFilterStatus;
        });
      }

      // Convert projects to the format expected by our table
      const formattedProjects = projectsToDisplay.map((project, index) => {
        // Get the latest status
        const latestStatus = getLatestProjectStatus(project);

        // Get customer name from customers Redux state - Improved approach
        let customerName = project.customerName;
        if (project.ClientId && customers.length > 0) {
          // Always look up customer from Redux store to ensure we have the latest data
          const matchingCustomer = customers.find(c => c.id === project.ClientId);
          if (matchingCustomer) {
            customerName = matchingCustomer.companyName;
          }
        }

        // Ensure we don't show "Client #ID" fallback
        if (!customerName || customerName.startsWith('Client #')) {
          customerName = extractCustomerName(project, customers);
        }

        return {
          // Always use manakProjectId if available to ensure consistency on refresh
          id: project.manakProjectId ? `${project.manakProjectId}` : `Project-${index + 1}`,
          status: formatStatusText(latestStatus),
          customerName: customerName,
          articleNo: project.projectNumber || `NT-${3015 + index}`,
          projectName: project.projectName || `Project ${index + 1}`,
          createdOn: {
            date: project.createdAt
              ? formatDateToDDMMYY(project.createdAt)
              : formatDateToDDMMYY(new Date()),
            time: extractTimeFromDate(project.createdAt),
          },
          // Include additional data as needed
          rawProject: project, // Include the raw project data for reference if needed
        };
      });

      // Apply pagination manually for Redux data
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const paginatedData = formattedProjects.slice(startIndex, endIndex);

      // Set the display data and total items for pagination
      setDisplayData(paginatedData);
      setTotalItems(formattedProjects.length);

      // Once we have data, set initialLoading to false
      setInitialLoading(false);
    } catch (error) {
      console.error('Error processing projects in applyFilterAndSetDisplayData:', error);
      setDisplayData([]);
      setTotalItems(0);
      setInitialLoading(false);
    }
  };

  // Function to handle edit project button click
  const handleEditProject = (row: any) => {
    console.log('Project row data:', row);
    console.log('Raw project data being passed to edit page:', row.rawProject);

    // Use the actual unique ID of the project
    const projectId = row.rawProject?.id || row.id;

    // Get the raw status - ensure we're using the most recent status
    let rawStatus = row.rawProject?.status || row.status;

    // Check projectAssigned array for most recent status
    if (row.rawProject?.projectAssigned && row.rawProject.projectAssigned.length > 0) {
      // Sort assignments by date (newest first)
      const sortedAssignments = [...row.rawProject.projectAssigned].sort(
        (a, b) =>
          new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
          new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
      );

      // Use the status from the newest assignment
      rawStatus = sortedAssignments[0].status || rawStatus;
      console.log('Using latest status for edit:', rawStatus);
    }

    // Get human readable formatted status
    const formattedStatus = formatStatusText(rawStatus);

    // Check if the current user is assigned to this project
    let isUserAssignedToProject = false;

    if (user?.id && row.rawProject) {
      // Check project assignment array
      if (row.rawProject.projectAssigned && row.rawProject.projectAssigned.length > 0) {
        isUserAssignedToProject = row.rawProject.projectAssigned.some(
          (assignment: any) =>
            assignment.adminId === user.id ||
            assignment.employeeId === user.id ||
            assignment.assignedToEmployee === user.id
        );
      }

      // Also check direct assignment fields as fallback
      if (!isUserAssignedToProject && row.rawProject.assignedEmployeeId === user.id) {
        isUserAssignedToProject = true;
      }

      if (!isUserAssignedToProject && row.rawProject.assignedToEmployee === user.id) {
        isUserAssignedToProject = true;
      }
    }

    console.log('Current user assigned to project:', isUserAssignedToProject);

    // Format the project data to ensure all needed fields are available
    const projectData = {
      ...row.rawProject,
      projectName: row.rawProject?.projectName || row.projectName,
      projectNumber: row.rawProject?.projectNumber || row.articleNo,
      status: rawStatus, // Send the original status code for backend compatibility
      displayStatus: formattedStatus, // Add the formatted display version
      ClientId: row.rawProject?.ClientId,
      projectDescription: row.rawProject?.projectDescription || '',
      comments: row.rawProject?.comments || '',
      projectAmount: row.rawProject?.projectAmount || 0,
      projectAssigned: row.rawProject?.projectAssigned || [],
      projectImages: row.rawProject?.projectImages || [],
      // Add explicit assignment information for the edit page
      userIsAssignedToThisProject: isUserAssignedToProject,
    };

    // Navigate to edit page with project ID in URL and all project data via state
    navigate(`/edit-project/${projectId}`, {
      state: {
        projectData: projectData,
      },
    });
  };

  // Function to handle create project button click
  const handleCreateProject = () => {
    navigate('/add-project'); // Navigate to add project page
  };

  // Add image viewer state
  const [viewerImage, setViewerImage] = useState<string>('');
  const [isImageViewerOpen, setIsImageViewerOpen] = useState<boolean>(false);

  // Function to open the image viewer
  const openImageViewer = (imageUrl: string) => {
    setViewerImage(imageUrl);
    setIsImageViewerOpen(true);
  };

  // Function to close the image viewer
  const closeImageViewer = () => {
    setIsImageViewerOpen(false);
  };

  // Table column definitions
  const columns = [
    {
      key: 'id',
      header: 'M ID',
      icon: <GoHash />,
      width: '7%',
      headerClassName: 'font-bold text-sm',
      truncate: true,
      renderCell: (value, row) => (
        <div className="flex items-center">
          <span className="flex-shrink-0">{value}</span>
          {row.rawProject?.projectRevisions && row.rawProject.projectRevisions.length > 0 ? (
            <span className="ml-2 bg-purple-500 text-white text-xs font-bold rounded-full px-2 py-0.5 flex-shrink-0">
              {row.rawProject.projectRevisions[0].formattedRevisionNumber ||
                `R${row.rawProject.projectRevisions[0].revisionNumber}`}
            </span>
          ) : row.rawProject?.revisionNumber > 0 ? (
            <span className="ml-2 bg-purple-500 text-white text-xs font-bold rounded-full px-2 py-0.5 flex-shrink-0">
              R{row.rawProject.revisionNumber}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'customerName',
      header: 'Company Name',
      icon: <CiUser />,
      width: '18%',
      headerClassName: 'font-bold text-sm',
      truncate: true,
    },
    {
      key: 'articleNo',
      header: 'Article No',
      icon: <FaRegFileAlt />,
      width: '12%',
      headerClassName: 'font-bold text-sm',
    },
    {
      key: 'projectName',
      header: 'Project Name',
      icon: <MdOutlineFeaturedPlayList />,
      width: '18%',
      headerClassName: 'font-bold text-sm',
      truncate: true,
    },
    {
      key: 'assignedTo',
      header: 'Assigned To',
      icon: <MdOutlineWorkOutline />,
      width: '15%',
      headerClassName: 'font-bold text-sm',
      truncate: true,
      renderCell: (_, row) => {
        const currentUserId = user?.id;

        // Use the assignedEmployeeName directly from API if available
        if (row.rawProject?.assignedEmployeeName) {
          const isSelfAssigned = row.rawProject.isSelfAssigned;

          // First check if this is the current user (either by self-assignment or by direct match)
          if (
            (isSelfAssigned && currentUserId === row.rawProject.assignedToEmployee) ||
            row.rawProject.assignedEmployeeId === currentUserId ||
            row.rawProject.assignedToEmployee === currentUserId
          ) {
            return (
              <div className="flex items-center">
                <span className="bg-lime-100 text-lime-800 px-2 py-1 rounded-full font-medium flex items-center">
                  <span className="mr-1">👤</span> You
                </span>
              </div>
            );
          }

          // If it's marked as self-assigned but not the current user, it's an admin
          if (isSelfAssigned) {
            return (
              <div className="flex items-center">
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium flex items-center">
                  <span className="mr-1">👑</span> {row.rawProject.assignedEmployeeName}
                </span>
              </div>
            );
          }

          return (
            <div className="flex items-center">
              <span className="text-gray-800">{row.rawProject.assignedEmployeeName}</span>
            </div>
          );
        }

        // Fallback to the old method if direct name is not available
        if (row.rawProject?.projectAssigned && row.rawProject.projectAssigned.length > 0) {
          const latestAssignment = row.rawProject.projectAssigned[0];

          // Check if this assignment is to the current user
          if (
            (latestAssignment.adminId && latestAssignment.adminId === currentUserId) ||
            (latestAssignment.employeeId && latestAssignment.employeeId === currentUserId)
          ) {
            return (
              <div className="flex items-center">
                <span className="bg-lime-100 text-lime-800 px-2 py-1 rounded-full font-medium flex items-center">
                  <span className="mr-1">👤</span> You
                </span>
              </div>
            );
          }

          // Check if assignment has assignedToName (from our API update)
          if (latestAssignment.assignedToName) {
            const isSelfAssigned = latestAssignment.adminId ? true : false;

            if (isSelfAssigned) {
              return (
                <div className="flex items-center">
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium flex items-center">
                    <span className="mr-1">👑</span> {latestAssignment.assignedToName}
                  </span>
                </div>
              );
            }

            return (
              <div className="flex items-center">
                <span className="text-gray-800">{latestAssignment.assignedToName}</span>
              </div>
            );
          }

          // Additional fallback to check adminId or employeeId directly if assignedToName is not available
          if (latestAssignment.adminId) {
            // Find admin name in employees array (admins are sometimes included there)
            const admin = employees.find(e => e.id === latestAssignment.adminId);
            return (
              <div className="flex items-center">
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium flex items-center">
                  <span className="mr-1">👑</span> {admin?.fullname || 'Admin'}
                </span>
              </div>
            );
          }

          if (latestAssignment.employeeId) {
            // Find employee by ID in employees array
            const employee = employees.find(e => e.id === latestAssignment.employeeId);
            if (employee) {
              return (
                <div className="flex items-center">
                  <span className="text-gray-800">{employee.fullname}</span>
                </div>
              );
            }
          }

          // Final fallback to assignedToEmployee field if it exists
          if (latestAssignment.assignedToEmployee) {
            const employee = employees.find(e => e.id === latestAssignment.assignedToEmployee);
            if (employee) {
              return (
                <div className="flex items-center">
                  <span className="text-gray-800">{employee.fullname}</span>
                </div>
              );
            }
          }
        }

        return <span className="text-gray-500 italic">Not assigned</span>;
      },
    },
    {
      key: 'createdOn',
      header: 'Created At',
      icon: <AiOutlineClockCircle />,
      width: '12%',
      headerClassName: 'font-bold text-sm',
    },
    {
      key: 'status',
      header: 'Status',
      icon: <PiRepeatFill />,
      width: '10%',
      headerClassName: 'font-bold text-sm',
    },
    {
      key: 'action',
      header: 'Action',
      width: '11%',
      headerClassName: 'font-bold text-sm',
      renderCell: (value, row) => {
        // Get the current project status for better error messaging
        let currentStatus = row.status;
        if (row.rawProject?.projectAssigned && row.rawProject.projectAssigned.length > 0) {
          const sortedAssignments = [...row.rawProject.projectAssigned].sort(
            (a, b) =>
              new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
              new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
          );
          if (sortedAssignments[0] && sortedAssignments[0].status) {
            currentStatus = sortedAssignments[0].status;
          }
        }

        // Normalize the status for display
        const normalizedStatus = normalizeStatus(currentStatus);
        const displayStatus = formatStatusText(normalizedStatus);

        // Check if user has permission to edit this project based on its status
        const canEdit = canEditProjectWithStatus(row);
        const isTransferred = isProjectTransferred(row);

        // Determine button style and functionality based on permissions
        const buttonClass = canEdit
          ? 'text-green-500 border border-green-500 rounded-full px-4 py-1 flex items-center'
          : 'text-gray-400 border border-gray-400 rounded-full px-4 py-1 flex items-center cursor-not-allowed opacity-60';

        // Determine tooltip message based on why user can't edit
        let tooltipMessage = 'Edit project';

        if (isTransferred) {
          tooltipMessage = "You can't edit this project as it has been reassigned to another user";
        } else if (!canEdit) {
          tooltipMessage = `You don't have permission to edit projects with ${displayStatus} status`;
        }

        // Create a detailed debug message for the console
        console.log(`Action button for project ${row.id || 'unknown'}:`, {
          status: currentStatus,
          normalizedStatus,
          displayStatus,
          canEdit,
          isTransferred,
          tooltipMessage,
        });

        return (
          <button
            className={buttonClass}
            onClick={e => {
              e.stopPropagation(); // Prevent row click event from triggering
              if (canEdit) {
                handleEditProject(row);
              } else {
                // Show toast message explaining why they can't edit
                toast.error(tooltipMessage, {
                  duration: 3000,
                  position: 'top-center',
                  icon: '🔒',
                });

                // Log detailed information about the permission issue
                console.log('Edit button clicked but disabled:', {
                  projectId: row.id,
                  projectName: row.projectName,
                  status: currentStatus,
                  normalizedStatus,
                  displayStatus,
                  isTransferred,
                  userPermissions: user?.permissions,
                });
              }
            }}
            disabled={!canEdit}
            title={tooltipMessage}
          >
            <TbEdit className="mr-1" /> Edit
          </button>
        );
      },
    },
  ];

  // Status colors mapping
  const statusColors = {
    // Formatted display versions
    'In Progress': 'bg-blue-100 text-blue-800',
    Delivered: 'bg-green-100 text-green-800',
    Review: 'bg-yellow-100 text-yellow-800',
    Assigned: 'bg-indigo-100 text-indigo-800',
    Billed: 'bg-red-100 text-red-800',
    Hold: 'bg-orange-100 text-orange-800',
    Cancel: 'bg-red-100 text-red-800',
    Cancelled: 'bg-red-100 text-red-800', // Fixed styling for Cancelled
    'Payment Received': 'bg-green-100 text-green-800',
    Revision: 'bg-purple-50 text-purple-700',
    Reopen: 'bg-purple-100 text-purple-800',
    Unassigned: 'bg-gray-100 text-gray-800',
    'Non Active': 'bg-gray-200 text-gray-700', // Improved styling for Non Active

    // Original backend versions (uppercase with underscores)
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    DELIVERED: 'bg-green-100 text-green-800',
    REVIEW: 'bg-yellow-100 text-yellow-800',
    ASSIGNED: 'bg-indigo-100 text-indigo-800',
    BILLED: 'bg-red-100 text-red-800',
    HOLD: 'bg-orange-100 text-orange-800',
    CANCELLED: 'bg-red-100 text-red-800',
    PAYMENT_RECEIVED: 'bg-green-100 text-green-800',
    REVISION: 'bg-purple-50 text-purple-700',
    REOPEN: 'bg-purple-100 text-purple-800',
    UNASSIGNED: 'bg-gray-100 text-gray-800',
    NON_ACTIVE: 'bg-gray-200 text-gray-700', // Improved styling for Non Active

    // Old format status values
    'For Review': 'bg-yellow-100 text-yellow-800',
    'Revision 2': 'bg-[#E2E3F5] text-[#2C2F61]',
    'On Hold': 'bg-orange-100 text-orange-800',
    ON_HOLD: 'bg-orange-100 text-orange-800',
  };

  // Status icons mapping
  const statusIcons = {
    // Formatted display versions
    'In Progress': <TbProgress className="w-4 h-4" />,
    Delivered: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    Review: <LiaNotesMedicalSolid className="w-4 h-4" />,
    Assigned: <BiReceipt className="w-4 h-4" />,
    Billed: <BiReceipt className="w-4 h-4" />,
    Hold: <AiOutlineClockCircle className="w-4 h-4" />,
    Cancel: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    Cancelled: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    'Payment Received': <BsCurrencyRupee className="w-4 h-4" />,
    Revision: <PiRepeatFill className="w-4 h-4" />,
    Reopen: <PiRepeatFill className="w-4 h-4" />,
    Unassigned: <MdOutlineWorkOutline className="w-4 h-4" />,
    'Non Active': <AiOutlineClockCircle className="w-4 h-4" />, // Using clock icon for expired warranty

    // Original backend versions (uppercase with underscores)
    IN_PROGRESS: <TbProgress className="w-4 h-4" />,
    DELIVERED: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    REVIEW: <LiaNotesMedicalSolid className="w-4 h-4" />,
    ASSIGNED: <BiReceipt className="w-4 h-4" />,
    BILLED: <BiReceipt className="w-4 h-4" />,
    HOLD: <AiOutlineClockCircle className="w-4 h-4" />,
    CANCELLED: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    PAYMENT_RECEIVED: <BsCurrencyRupee className="w-4 h-4" />,
    REVISION: <PiRepeatFill className="w-4 h-4" />,
    REOPEN: <PiRepeatFill className="w-4 h-4" />,
    UNASSIGNED: <MdOutlineWorkOutline className="w-4 h-4" />,
    NON_ACTIVE: <AiOutlineClockCircle className="w-4 h-4" />, // Using clock icon for expired warranty

    // Old format status values
    'In-Progress': <TbProgress className="w-4 h-4" />,
    'For Review': <LiaNotesMedicalSolid className="w-4 h-4" />,
  };

  // Helper function to check if project was transferred from current user to another user
  const isProjectTransferred = (project: any): boolean => {
    if (
      !project?.rawProject?.projectAssigned ||
      project.rawProject.projectAssigned.length < 2 ||
      !user?.id
    ) {
      return false;
    }

    // Sort assignments by date (newest first)
    const sortedAssignments = [...project.rawProject.projectAssigned].sort((a, b) => {
      const dateA = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
      const dateB = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
      return dateA - dateB;
    });

    // Get the most recent and second most recent assignments
    const currentAssignment = sortedAssignments[0];
    const previousAssignment = sortedAssignments[1];

    // Check if current user is not assigned in the current assignment
    const currentUserNotAssignedNow = !(
      currentAssignment.adminId === user.id ||
      currentAssignment.employeeId === user.id ||
      currentAssignment.assignedToEmployee === user.id
    );

    // Check if current user was assigned in the previous assignment
    const currentUserWasAssignedBefore =
      previousAssignment.adminId === user.id ||
      previousAssignment.employeeId === user.id ||
      previousAssignment.assignedToEmployee === user.id;

    // Project was transferred if user was previously assigned but is not assigned now
    return currentUserNotAssignedNow && currentUserWasAssignedBefore;
  };

  // Helper function to check if user has permission to edit a project based on its status
  const canEditProjectWithStatus = (project: any): boolean => {
    console.log('canEditProjectWithStatus - Checking project:', project.id || 'unknown id');

    // If project was transferred, user can't edit it
    if (isProjectTransferred(project)) {
      console.log('canEditProjectWithStatus - Project was transferred, cannot edit');
      return false;
    }

    // Per new requirements: Always allow editing regardless of project status or user permissions
    console.log(
      'canEditProjectWithStatus - Status check removed, allowing edit regardless of status permissions'
    );

    // Return true to allow editing regardless of project status
    return true;
  };

  // Helper function to handle row click
  const handleRowClick = (row: any) => {
    console.log('Row clicked:', row);

    // Set the selected project
    setSelectedProject(row);

    // Format assignment history for display
    if (row.rawProject?.projectAssigned && row.rawProject.projectAssigned.length > 0) {
      // Use the existing formatAssignmentHistory function with the correct parameters
      const history = formatAssignmentHistory(row.rawProject, employees, user);
      setAssignmentHistory(history);
    } else {
      setAssignmentHistory([]);
    }

    // Reset assignment history visibility
    setShowAssignmentHistory(false);

    // Open the popup
    projectDetailsPopup.openPopup();
  };

  // Check for project permissions on mount
  useEffect(() => {
    console.log('[MyProjects] User data from Redux:', user);
    console.log('[MyProjects] User ID:', user?.id);

    if (user) {
      // Super Admin has all permissions
      if (user.designation === 'superAdmin') {
        setHasProjectPermission(true);
      } else if (user.permissions) {
        let projectReadValue = false;
        let projectWriteValue = false;
        let projectEditValue = false;

        // Check if permissions is an object with numeric keys (like {0: {...}})
        if (typeof user.permissions === 'object' && '0' in user.permissions) {
          //@ts-ignore
          projectReadValue = Boolean(user.permissions[0].projectRead);
          //@ts-ignore
          projectWriteValue = Boolean(user.permissions[0].projectWrite);
          //@ts-ignore
          projectEditValue = Boolean(user.permissions[0].projectEdit);
        }
        // Check if permissions is an array
        else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          projectReadValue = Boolean(user.permissions[0].projectRead);
          projectWriteValue = Boolean(user.permissions[0].projectWrite);
          projectEditValue = Boolean(user.permissions[0].projectEdit);
        }
        // Direct object access if neither of the above
        else if (typeof user.permissions === 'object') {
          projectReadValue = Boolean(user.permissions.projectRead);
          projectWriteValue = Boolean(user.permissions.projectWrite);
          projectEditValue = Boolean(user.permissions.projectEdit);
        }

        // If user has any project permission, they can view projects
        const hasAnyProjectPermission = projectReadValue || projectWriteValue || projectEditValue;
        setHasProjectPermission(hasAnyProjectPermission);
      } else {
        // No permissions object means no access
        setHasProjectPermission(false);
      }
    } else {
      // Default to no permission if user is not defined
      setHasProjectPermission(false);
    }
  }, [user]);

  // Add state for project popup
  const projectDetailsPopup = usePopup();
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [viewingOriginProject, setViewingOriginProject] = useState(false);
  const [viewingReopenedProject, setViewingReopenedProject] = useState(false);
  const [showOriginAssignmentHistory, setShowOriginAssignmentHistory] = useState(false);
  const [showReopenedAssignmentHistory, setShowReopenedAssignmentHistory] = useState(false);
  const [showReopenedProjectImages, setShowReopenedProjectImages] = useState(false);
  const [selectedReopenedImage, setSelectedReopenedImage] = useState<string>('');

  // Debug function to check Redux store state
  const debugReduxStore = () => {
    const state = store.getState();
    console.log('[DEBUG] Full Redux Store:', state);
    console.log('[DEBUG] userProjects state:', state.userProjects);
    toast.success('Redux store state logged to console');
  };

  // Handle refresh button click
  const handleRefreshProjects = () => {
    console.log(
      '[MyProjects] Manually refreshing projects for status:',
      statusFilter,
      'page:',
      currentPage
    );

    if (user?.id) {
      // Clear current data to show loading immediately
      setDisplayData([]);

      if (statusFilter === 'All') {
        // For "All" status, clear entire user projects cache and reload
        dispatch({ type: 'userProjects/clearUserProjectsCache' });

        // Refresh all user projects maintaining current page
        refreshProjects()
          .then(() => {
            console.log(
              '[MyProjects] Refresh successful, fetching data for current page:',
              currentPage
            );
            // Use the unified fetchProjects function which handles API calls and formatting
            return fetchProjects(currentPage, itemsPerPage, statusFilter, searchValue);
          })
          .then(() => {
            toast.success('My projects refreshed');
          })
          .catch(error => {
            console.error('[MyProjects] Error refreshing projects:', error);
            toast.error('Failed to refresh projects');
          });
      } else {
        // For specific status, clear that status cache and reload
        dispatch({ type: 'userProjects/clearSpecificStatusCache', payload: statusFilter });

        // Use the unified fetchProjects function which will handle the status filter correctly
        fetchProjects(currentPage, itemsPerPage, statusFilter, searchValue)
          .then(() => {
            toast.success(`${statusFilter} projects refreshed`);
          })
          .catch(error => {
            console.error('[MyProjects] Error refreshing filtered projects:', error);
            toast.error('Failed to refresh projects');
          });
      }
    } else {
      toast.error('User not logged in');
    }
  };

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [visibleCustomers, setVisibleCustomers] = useState<any[]>([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [page, setPage] = useState(1);

  // Customer details popup state

  return (
    <React.Fragment>
      <ComponentWrapper
        icon={<MdOutlineFeaturedPlayList size={30} />}
        title="My Projects"
        buttonOne={
          <>
            <Button
              variant="secondary"
              size="sm"
              className="py-2 mr-3"
              icon={<MdRefresh size={16} />}
              onClick={handleRefreshProjects}
            >
              Refresh
            </Button>
            {/* Only show Create Project button if user has projectWrite permission */}
            {(() => {
              // Check if user is superAdmin or has projectWrite permission
              let hasProjectWritePermission = false;

              if (user?.designation === 'superAdmin') {
                hasProjectWritePermission = true;
              } else if (user?.permissions) {
                // Check if permissions is an object with numeric keys (like {0: {...}})
                if (typeof user.permissions === 'object' && '0' in user.permissions) {
                  //@ts-ignore
                  hasProjectWritePermission = Boolean(user.permissions[0].projectWrite);
                }
                // Check if permissions is an array
                else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
                  hasProjectWritePermission = Boolean(user.permissions[0].projectWrite);
                }
                // Direct object access if neither of the above
                else if (typeof user.permissions === 'object') {
                  hasProjectWritePermission = Boolean(user.permissions.projectWrite);
                }
              }

              return hasProjectWritePermission ? (
                <Button
                  variant="primary"
                  size="sm"
                  className="py-2 mr-3 text-white"
                  icon={<FiPlus size={16} />}
                  onClick={handleCreateProject}
                >
                  Create Project
                </Button>
              ) : null;
            })()}
          </>
        }
        buttonsTwo={[
          {
            component: hasProjectPermission ? (
              <Button
                variant="secondary"
                size="sm"
                className="py-2 ml-1" // Keeping the margin-left for spacing
                icon={<RxDownload size={16} />}
                onClick={async () => {
                  toast.success('Downloading project data as Excel file...');
                  try {
                    // Import axios instance
                    const axiosInstance = (await import('../axios')).default;

                    // Make authenticated request to get the Excel file
                    const response = await axiosInstance.get('/v1/admin/project/export-excel', {
                      responseType: 'blob', // Important for file downloads
                    });

                    // Create a blob from the response data
                    const blob = new Blob([response.data], {
                      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });

                    // Create a temporary URL for the blob
                    const url = window.URL.createObjectURL(blob);

                    // Create a temporary link element
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'projects.xlsx';

                    // Append to the document, click it, and remove it
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // Release the blob URL
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Error downloading data:', error);
                    toast.error('There was a problem downloading the data. Please try again.');
                  }
                }}
              >
                Download Data
              </Button>
            ) : null,
          },
        ]}
      >
        {/* Create a flex container for slider and search/filter */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
          {/* Left side: Project Filter Slider */}
          <div className="w-full md:flex-1 md:max-w-[65%] flex items-center">
            <div className="w-full">
              <ProjectFilterSlider
                key={`project-filter-${statusFilter}`}
                onFilterChange={handleStatusFilterChange}
                defaultFilter={statusFilter}
              />
            </div>
          </div>

          {/* Right side: Search and Filter (takes remaining space) */}
          <div className="w-full md:flex-1 md:max-w-[33%] flex items-center">
            <div className="w-full">
              <TableSearchFilter
                searchPlaceholder="Search projects..."
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                onClearSearch={() => setSearchValue('')}
                fullWidthSearch={true}
                showFilterButton={false}
              />
            </div>
          </div>
        </div>

        {/* Use our reusable FilterPanel component */}
        {showFilterPanel && (
          <FilterPanel
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            statusOptions={[
              { value: 'All', label: 'All' },
              { value: 'IN_PROGRESS', label: 'In Progress', color: 'rgb(219 234 254)' },
              { value: 'DELIVERED', label: 'Delivered', color: 'rgb(220 252 231)' },
              { value: 'REVIEW', label: 'Review', color: 'rgb(254 249 195)' },
              { value: 'ASSIGNED', label: 'Assigned', color: 'rgb(224 231 255)' },
              { value: 'BILLED', label: 'Billed', color: 'rgb(254 226 226)' },
              { value: 'HOLD', label: 'On Hold', color: 'rgb(255 237 213)' },
              { value: 'CANCELLED', label: 'Cancelled', color: 'rgb(254 226 226)' },
              { value: 'PAYMENT_RECEIVED', label: 'Payment Received', color: 'rgb(220 252 231)' },
              { value: 'REOPEN', label: 'Reopen', color: 'rgb(243 232 255)' },
              { value: 'UNASSIGNED', label: 'Unassigned', color: 'rgb(243 244 246)' },
              { value: 'NON_ACTIVE', label: 'Non Active', color: 'rgb(156 163 175)' },
            ]}
            className="mt-3 mb-6"
          />
        )}

        {/* Table display */}
        <div className="bg-transparent md:bg-white rounded-lg">
          {/* Show skeleton loading during initial load, filter change, or when loading new page data */}
          {initialLoading ||
            filterLoading ||
            loading ||
            (displayData.length === 0 && userProjectsLoading) ? (
            <SkeletonTable rows={itemsPerPage} />
          ) : displayData.length > 0 ? (
            <ResponsiveTable
              data={displayData}
              columns={columns}
              onRowClick={handleRowClick}
              statusColors={statusColors}
              statusIcons={statusIcons}
              itemsPerPageOptions={[10, 15, 20, 50]}
              defaultItemsPerPage={itemsPerPage}
              totalItems={
                pagination
                  ? (pagination as any).total || (pagination as any).totalItems || totalItems
                  : totalItems
              }
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={page => {
                console.log(
                  `[MyProjects] Page change requested: ${currentPage} -> ${page} (statusFilter: ${statusFilter})`
                );

                // Immediately clear current data and show loading
                setDisplayData([]);
                setLoading(true);

                // Update current page
                setCurrentPage(page);

                // Fetch new page of data from API (will respect current status filter)
                fetchProjects(page, itemsPerPage, statusFilter, searchValue);
              }}
              onItemsPerPageChange={size => {
                console.log(
                  `[MyProjects] Items per page change requested: ${itemsPerPage} -> ${size} (statusFilter: ${statusFilter})`
                );

                // Immediately clear current data and show loading
                setDisplayData([]);
                setLoading(true);

                // Update settings
                setItemsPerPage(size);
                setCurrentPage(1);

                // Fetch with new page size (will respect current status filter)
                fetchProjects(1, size, statusFilter, searchValue);
              }}
              isLoading={filterLoading} // Pass loading state to table component
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              {userProjectsLoading ? (
                <SkeletonTable rows={5} /> // Show skeleton while loading
              ) : userProjectsError ? (
                `Error loading projects: ${userProjectsError}`
              ) : (
                <div className="flex flex-col items-center space-y-4">
                  <p className="text-lg">No assigned projects found</p>
                  <p className="text-sm max-w-lg">
                    You don't have any projects assigned to you yet. Projects will appear here when
                    they are assigned to you.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </ComponentWrapper>

      {/* Project Details Popup */}
      {selectedProject && (
        <PopupWrapper
          title={`M${viewingReopenedProject && reopenedProject ? formattedReopenedProject.id : viewingOriginProject && originProject ? formattedOriginProject.id : selectedProject.id}`}
          isOpen={projectDetailsPopup.isOpen}
          onClose={() => {
            projectDetailsPopup.closePopup();
            // Reset both origin and reopened project views when closing
            handleCloseOriginProject();
            handleCloseReopenedProject();
          }}
          size="lg"
          logo={
            viewingReopenedProject ? (
              <button
                onClick={handleCloseReopenedProject}
                className="flex items-center justify-center w-14 h-14 hover:bg-blue-50 rounded-full transition-colors"
                title="Back to current project"
              >
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            ) : viewingOriginProject ? (
              <button
                onClick={handleCloseOriginProject}
                className="flex items-center justify-center w-14 h-14 hover:bg-blue-50 rounded-full transition-colors"
                title="Back to current project"
              >
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            ) : (
              <FiFileText size={53} className="text-blue-600" />
            )
          }
          headerComponent={
            <div
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm ${statusColors[(viewingReopenedProject && reopenedProject ? formattedReopenedProject.status : viewingOriginProject && originProject ? formattedOriginProject.status : selectedProject.status)] || 'bg-gray-100 text-gray-800'}`}
            >
              <span className="w-4 h-4">{statusIcons[(viewingReopenedProject && reopenedProject ? formattedReopenedProject.status : viewingOriginProject && originProject ? formattedOriginProject.status : selectedProject.status)]}</span>
              <span>
                {viewingReopenedProject && reopenedProject
                  ? (formattedReopenedProject.displayStatus || formatStatusText(formattedReopenedProject.status))
                  : viewingOriginProject && originProject
                    ? (formattedOriginProject.displayStatus || formatStatusText(formattedOriginProject.status))
                    : (selectedProject.displayStatus || formatStatusText(selectedProject.status))}
              </span>
            </div>
          }
        >
          {/* Dual View Container with Sliding Animation */}
          <div className="relative overflow-hidden">
            <div
              className={`flex transition-transform duration-500 ease-in-out ${viewingReopenedProject ? 'translate-x-[-100%]' : viewingOriginProject ? '-translate-x-1/2' : 'translate-x-0'
                }`}
              style={{ width: viewingReopenedProject ? '200%' : viewingOriginProject ? '200%' : '100%' }}
            >
              {/* Current Project View */}
              <div className={`${viewingReopenedProject || viewingOriginProject ? 'w-1/2 pr-6' : 'w-full'} flex-shrink-0`}>
                {/* Current Project Header */}
                <div className="flex flex-col pb-4">
                  <div className="text-sm text-gray-500">
                    Last updated on - {selectedProject.rawProject?.projectAssigned?.[0]?.assignedDate
                      ? new Date(selectedProject.rawProject.projectAssigned[0].assignedDate).toLocaleDateString()
                      : new Date().toLocaleDateString()} (
                    {selectedProject.rawProject?.projectAssigned?.[0]?.assignedDate
                      ? new Date(selectedProject.rawProject.projectAssigned[0].assignedDate).toLocaleTimeString()
                      : new Date().toLocaleTimeString()})
                  </div>
                  {!viewingOriginProject && !viewingReopenedProject && selectedProject.rawProject?.originProject && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
                        <PiRepeatFill className="w-4 h-4" />
                        <span>Reopen</span>
                      </span>
                      <span className="text-sm text-gray-600">from</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Button clicked!', selectedProject.rawProject.originProject);
                          handleViewOriginProject(selectedProject.rawProject.originProject);
                        }}
                        className="text-sm text-blue-600 font-semibold hover:text-blue-800 hover:underline transition-colors"
                      >
                        M{selectedProject.rawProject.originProject.manakProjectId}
                      </button>
                    </div>
                  )}
                  {!viewingOriginProject && !viewingReopenedProject && selectedProject.rawProject?.reopenedProjects && selectedProject.rawProject.reopenedProjects.length > 0 && selectedProject.rawProject.reopenedProjects.some((rp: any) => rp.isChargeable) && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                        <PiRepeatFill className="w-4 h-4" />
                        <span>Reopen</span>
                      </span>
                      <span className="text-sm text-gray-600">to</span>
                      {selectedProject.rawProject.reopenedProjects
                        .filter((rp: any) => rp.isChargeable)
                        .map((reopenedProj: any, idx: number) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('Reopened project button clicked:', reopenedProj);
                              handleViewReopenedProject(reopenedProj);
                            }}
                            className="text-sm text-blue-600 font-semibold hover:text-blue-800 hover:underline transition-colors"
                          >
                            M{reopenedProj.manakProjectId}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col py-6">
                  <div className="grid grid-cols-2 gap-6">
                    <InputField
                      label="Company Name"
                      value={selectedProject.customerName}
                      className="bg-gray-100"
                      disabled
                      icon={<CiUser className="w-5 h-5" />}
                    />
                    <InputField
                      label="Article Number"
                      value={selectedProject.articleNo}
                      className="bg-gray-100"
                      disabled
                      icon={<FaRegFileAlt className="w-5 h-5" />}
                    />
                    <InputField
                      label="Project Name"
                      value={selectedProject.projectName}
                      className="bg-gray-100"
                      disabled
                      icon={<FiFileText className="w-5 h-5" />}
                    />
                    <InputField
                      label="Assigned To"
                      value={(() => {
                        // Use assignedEmployeeName directly if available from API
                        if (selectedProject.rawProject?.assignedEmployeeName) {
                          const isSelfAssigned = selectedProject.rawProject.isSelfAssigned;
                          return isSelfAssigned
                            ? `${selectedProject.rawProject.assignedEmployeeName} (Self Assigned)`
                            : selectedProject.rawProject.assignedEmployeeName;
                        }

                        // Fallback to the assignment data if directly provided name is not available
                        if (
                          selectedProject.rawProject?.projectAssigned &&
                          selectedProject.rawProject.projectAssigned.length > 0
                        ) {
                          const latestAssignment = selectedProject.rawProject.projectAssigned[0];

                          // Check if assignment has assignedToName from updated API
                          if (latestAssignment.assignedToName) {
                            return latestAssignment.adminId
                              ? `${latestAssignment.assignedToName} (Self Assigned)`
                              : latestAssignment.assignedToName;
                          }
                        }

                        return selectedProject.assignedTo || 'Not assigned';
                      })()}
                      className="bg-gray-100"
                      disabled
                      icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                    />
                    <InputField
                      label="Received On"
                      value={new Date(
                        selectedProject.rawProject?.receivedOn || Date.now()
                      ).toLocaleDateString()}
                      className="bg-gray-100"
                      disabled
                      icon={<AiOutlineClockCircle className="w-5 h-5" />}
                    />

                    {/* Render warranty fields only for BILLED projects with warranty dates */}
                    {selectedProject.status === 'Billed' && selectedProject.rawProject?.warrantyUntil ? (
                      <>
                        <InputField
                          label="Bill Number"
                          value={selectedProject.rawProject?.billNumber || 'Not specified'}
                          className="bg-gray-100"
                          disabled
                          icon={<BiReceipt className="w-5 h-5" />}
                        />
                        <div className="relative">
                          <InputField
                            label="Warranty Until"
                            value={new Date(
                              selectedProject.rawProject.warrantyUntil
                            ).toLocaleDateString()}
                            className={`bg-gray-100 ${new Date(selectedProject.rawProject.warrantyUntil) > new Date()
                              ? 'border-green-500 text-green-700'
                              : 'border-red-500 text-red-700'
                              }`}
                            disabled
                            icon={<AiOutlineClockCircle className="w-5 h-5" />}
                          />
                          <div
                            className={`absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold ${new Date(selectedProject.rawProject.warrantyUntil) > new Date()
                              ? 'bg-green-100 text-green-700 border border-green-500'
                              : 'bg-red-100 text-red-700 border border-red-500'
                              }`}
                            style={{ margin: 0 }}
                          >
                            {new Date(selectedProject.rawProject.warrantyUntil) > new Date()
                              ? 'Active'
                              : 'Expired'}
                          </div>
                        </div>
                        <InputField
                          label="Billed Date"
                          value={new Date(
                            selectedProject.rawProject.projectAssigned.find(
                              (a: any) => a.status === 'BILLED'
                            )?.assignedDate || selectedProject.rawProject.projectAssigned[0].assignedDate
                          ).toLocaleDateString()}
                          className="bg-gray-100"
                          disabled
                          icon={<BiReceipt className="w-5 h-5" />}
                        />
                        <div className="col-span-2">
                          <button
                            onClick={() => setShowAssignmentHistory(!showAssignmentHistory)}
                            className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                        flex items-center justify-between
                        ${showAssignmentHistory
                                ? 'bg-blue-50 border-blue-600 text-blue-700'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            <div className="flex items-center gap-2">
                              <FiClock className="h-5 w-5" />
                              <span className="font-medium">View History</span>
                            </div>
                            {showAssignmentHistory ? (
                              <FiChevronUp className="h-5 w-5" />
                            ) : (
                              <FiChevronDown className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </>
                    ) : (
                      // View History Button (only shown if warranty fields are not displayed)
                      <button
                        onClick={() => setShowAssignmentHistory(!showAssignmentHistory)}
                        className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                    flex items-center justify-between
                    ${showAssignmentHistory
                            ? 'bg-blue-50 border-blue-600 text-blue-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center gap-2">
                          <FiClock className="h-5 w-5" />
                          <span className="font-medium">View History</span>
                        </div>
                        {showAssignmentHistory ? (
                          <FiChevronUp className="h-5 w-5" />
                        ) : (
                          <FiChevronDown className="h-5 w-5" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Assignment History Table */}
                  <div
                    className={`mt-3 transition-all duration-300 ${showAssignmentHistory ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
                  >
                    <TaskAssignmentTable
                      assignments={assignmentHistory}
                      visible={showAssignmentHistory}
                    />
                  </div>

                  {/* Product Attachments */}
                  <div className="py-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Product Attachments</h3>
                    <div className="flex flex-wrap gap-4">
                      {selectedProject.rawProject?.profilePicture &&
                        selectedProject.rawProject.profilePicture.trim() !== '' &&
                        !selectedProject.rawProject.profilePicture.includes('default-project-image.png') ? (
                        <div className="relative">
                          <img
                            src={selectedProject.rawProject.profilePicture}
                            alt={`Project ${selectedProject.projectName} Image`}
                            className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => openImageViewer(selectedProject.rawProject.profilePicture)}
                          />
                        </div>
                      ) : selectedProject.rawProject?.projectImages?.length > 0 ? (
                        selectedProject.rawProject.projectImages.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={image}
                              alt={`Project Attachment ${index + 1}`}
                              className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => openImageViewer(image)}
                            />
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500">No attachments available</p>
                      )}
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="mt-6">
                    {(() => {
                      // Get the current project status for better error messaging
                      let currentStatus = selectedProject.status;

                      if (
                        selectedProject.rawProject?.projectAssigned &&
                        selectedProject.rawProject.projectAssigned.length > 0
                      ) {
                        const sortedAssignments = [...selectedProject.rawProject.projectAssigned].sort(
                          (a, b) =>
                            new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
                            new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
                        );
                        if (sortedAssignments[0] && sortedAssignments[0].status) {
                          currentStatus = sortedAssignments[0].status;
                        }
                      }

                      // Normalize the status for display
                      const normalizedStatus = normalizeStatus(currentStatus);
                      const displayStatus = formatStatusText(normalizedStatus);

                      // Check if user has permission to edit this project based on its status
                      const canEdit = canEditProjectWithStatus(selectedProject);
                      const isTransferred = isProjectTransferred(selectedProject);

                      // Determine tooltip message based on why user can't edit
                      let tooltipMessage = 'Edit project';

                      if (isTransferred) {
                        tooltipMessage =
                          "You can't edit this project as it has been reassigned to another user";
                      } else if (!canEdit) {
                        tooltipMessage = `You don't have permission to edit projects with ${displayStatus} status`;
                      }

                      // Create a detailed debug message for the console
                      console.log(`Popup action button for project ${selectedProject.id || 'unknown'}:`, {
                        status: currentStatus,
                        normalizedStatus,
                        displayStatus,
                        canEdit,
                        isTransferred,
                        tooltipMessage,
                      });

                      return (
                        <button
                          onClick={() => {
                            if (canEdit) {
                              projectDetailsPopup.closePopup();
                              handleEditProject(selectedProject);
                            } else {
                              // Show toast message explaining why they can't edit
                              toast.error(tooltipMessage, {
                                duration: 3000,
                                position: 'top-center',
                                icon: '🔒',
                              });

                              // Log detailed information about the permission issue
                              console.log('Popup edit button clicked but disabled:', {
                                projectId: selectedProject.id,
                                projectName: selectedProject.projectName,
                                status: currentStatus,
                                normalizedStatus,
                                displayStatus,
                                isTransferred,
                                userPermissions: user?.permissions,
                              });
                            }
                          }}
                          className={`w-full px-4 py-4 text-lg font-semibold rounded-lg flex items-center border-2 justify-center gap-2 ${canEdit
                            ? 'bg-lime-100 hover:bg-lime-200 border-lime-600 text-lime-700'
                            : 'bg-gray-100 border-gray-400 text-gray-400 cursor-not-allowed opacity-60'
                            }`}
                          title={tooltipMessage}
                          disabled={!canEdit}
                        >
                          <TbEdit className="mr-1" /> Edit Project
                        </button>
                      );
                    })()}
                  </div>
                </div>
                {/* End of Current Project View */}
              </div>

              {/* Origin Project View */}
              {viewingOriginProject && originProject && (
                <div className="w-1/2 flex-shrink-0 pl-6 border-l-2 border-gray-200">
                  {/* Origin Project Header */}
                  <div className="flex flex-col pb-4">
                    <div className="text-sm text-gray-500">
                      Last updated on - {formattedOriginProject?.rawProject?.projectAssigned?.[0]?.assignedDate
                        ? new Date(formattedOriginProject.rawProject.projectAssigned[0].assignedDate).toLocaleDateString()
                        : new Date().toLocaleDateString()} (
                      {formattedOriginProject?.rawProject?.projectAssigned?.[0]?.assignedDate
                        ? new Date(formattedOriginProject.rawProject.projectAssigned[0].assignedDate).toLocaleTimeString()
                        : new Date().toLocaleTimeString()})
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                        <span>Origin Project</span>
                      </span>
                    </div>
                  </div>

                  {originProjectLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="flex flex-col py-6">
                      <div className="grid grid-cols-2 gap-6">
                        <InputField
                          label="Company Name"
                          value={formattedOriginProject?.customerName || 'N/A'}
                          className="bg-gray-100"
                          disabled
                          icon={<CiUser className="w-5 h-5" />}
                        />
                        <InputField
                          label="Article Number"
                          value={formattedOriginProject?.articleNo || 'N/A'}
                          className="bg-gray-100"
                          disabled
                          icon={<FaRegFileAlt className="w-5 h-5" />}
                        />
                        <InputField
                          label="Project Name"
                          value={formattedOriginProject?.projectName || 'N/A'}
                          className="bg-gray-100"
                          disabled
                          icon={<FiFileText className="w-5 h-5" />}
                        />
                        <InputField
                          label="Assigned To"
                          value={(() => {
                            if (formattedOriginProject.rawProject?.assignedEmployeeName) {
                              const isSelfAssigned = formattedOriginProject.rawProject.isSelfAssigned;
                              return isSelfAssigned
                                ? `${formattedOriginProject.rawProject.assignedEmployeeName} (Self Assigned)`
                                : formattedOriginProject.rawProject.assignedEmployeeName;
                            }

                            if (
                              formattedOriginProject.rawProject?.projectAssigned &&
                              formattedOriginProject.rawProject.projectAssigned.length > 0
                            ) {
                              const latestAssignment = formattedOriginProject.rawProject.projectAssigned[0];

                              if (latestAssignment.assignedToName) {
                                return latestAssignment.adminId
                                  ? `${latestAssignment.assignedToName} (Self Assigned)`
                                  : latestAssignment.assignedToName;
                              }
                            }

                            return 'Not specified';
                          })()}
                          className="bg-gray-100"
                          disabled
                          icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                        />
                        <InputField
                          label="Received On"
                          value={new Date(
                            formattedOriginProject.rawProject?.receivedOn ||
                            formattedOriginProject.rawProject?.createdAt ||
                            Date.now()
                          ).toLocaleDateString()}
                          className="bg-gray-100"
                          disabled
                          icon={<AiOutlineClockCircle className="w-5 h-5" />}
                        />

                        {/* Warranty fields for origin project if applicable */}
                        {formattedOriginProject.status === 'Billed' && formattedOriginProject.rawProject?.warrantyUntil ? (
                          <>
                            <InputField
                              label="Bill Number"
                              value={formattedOriginProject.rawProject?.billNumber || 'Not specified'}
                              className="bg-gray-100"
                              disabled
                              icon={<BiReceipt className="w-5 h-5" />}
                            />
                            <div className="relative">
                              <InputField
                                label="Warranty Until"
                                value={new Date(formattedOriginProject.rawProject.warrantyUntil).toLocaleDateString()}
                                className={`bg-gray-100 ${new Date(formattedOriginProject.rawProject.warrantyUntil) > new Date()
                                  ? 'border-green-500 text-green-700'
                                  : 'border-red-500 text-red-700'
                                  }`}
                                disabled
                                icon={<AiOutlineClockCircle className="w-5 h-5" />}
                              />
                              <div
                                className={`absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold ${new Date(formattedOriginProject.rawProject.warrantyUntil) > new Date()
                                  ? 'bg-green-100 text-green-700 border border-green-500'
                                  : 'bg-red-100 text-red-700 border border-red-500'
                                  }`}
                                style={{ margin: 0 }}
                              >
                                {new Date(formattedOriginProject.rawProject.warrantyUntil) > new Date() ? 'Active' : 'Expired'}
                              </div>
                            </div>
                            <InputField
                              label="Billed Date"
                              value={new Date(
                                formattedOriginProject.rawProject.projectAssigned.find(
                                  (a: any) => a.status === 'BILLED'
                                )?.assignedDate || formattedOriginProject.rawProject.projectAssigned[0].assignedDate
                              ).toLocaleDateString()}
                              className="bg-gray-100"
                              disabled
                              icon={<BiReceipt className="w-5 h-5" />}
                            />
                            <div className="col-span-2">
                              <button
                                onClick={() => setShowOriginAssignmentHistory(!showOriginAssignmentHistory)}
                                className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                                  flex items-center justify-between
                                  ${showOriginAssignmentHistory
                                    ? 'bg-blue-50 border-blue-600 text-blue-700'
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                  }`}
                              >
                                <div className="flex items-center gap-2">
                                  <FiClock className="h-5 w-5" />
                                  <span className="font-medium">View History</span>
                                </div>
                                {showOriginAssignmentHistory ? (
                                  <FiChevronUp className="h-5 w-5" />
                                ) : (
                                  <FiChevronDown className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                          </>
                        ) : (
                          // View History Button (only shown if warranty fields are not displayed)
                          <button
                            onClick={() => setShowOriginAssignmentHistory(!showOriginAssignmentHistory)}
                            className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                              flex items-center justify-between
                              ${showOriginAssignmentHistory
                                ? 'bg-blue-50 border-blue-600 text-blue-700'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            <div className="flex items-center gap-2">
                              <FiClock className="h-5 w-5" />
                              <span className="font-medium">View History</span>
                            </div>
                            {showOriginAssignmentHistory ? (
                              <FiChevronUp className="h-5 w-5" />
                            ) : (
                              <FiChevronDown className="h-5 w-5" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Assignment History Table */}
                      <div
                        className={`mt-3 transition-all duration-300 ${showOriginAssignmentHistory ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                          }`}
                      >
                        <TaskAssignmentTable
                          assignments={originAssignmentHistory}
                          visible={showOriginAssignmentHistory}
                        />
                      </div>

                      {/* Product Attachments */}
                      <div className="py-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Product Attachments</h3>
                        <div className="flex flex-wrap gap-4">
                          {formattedOriginProject.rawProject?.profilePicture &&
                            formattedOriginProject.rawProject.profilePicture.trim() !== '' &&
                            !formattedOriginProject.rawProject.profilePicture.includes('default-project-image.png') ? (
                            <div className="relative">
                              <img
                                src={formattedOriginProject.rawProject.profilePicture}
                                alt={`Project ${formattedOriginProject.projectName} Image`}
                                className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => openImageViewer(formattedOriginProject.rawProject.profilePicture)}
                              />
                            </div>
                          ) : formattedOriginProject.rawProject?.projectImages?.length > 0 ? (
                            formattedOriginProject.rawProject.projectImages.map((image: any, index: number) => (
                              <div key={index} className="relative">
                                <img
                                  src={image}
                                  alt={`Project Attachment ${index + 1}`}
                                  className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => openImageViewer(image)}
                                />
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500">No attachments available</p>
                          )}
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="mt-6">
                        <button
                          onClick={() => {
                            projectDetailsPopup.closePopup();
                            handleEditProject(formattedOriginProject);
                          }}
                          className="w-full px-4 py-4 text-lg font-semibold rounded-lg flex items-center border-2 justify-center gap-2 bg-lime-100 hover:bg-lime-200 border-lime-600 text-lime-700"
                          title="Edit origin project"
                        >
                          <TbEdit className="mr-1" /> Edit Project
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Reopened Project View */}
              {viewingReopenedProject && reopenedProject && (
                <div className="w-1/2 flex-shrink-0 pl-6 border-l-2 border-gray-200">
                  {/* Reopened Project Header */}
                  <div className="flex flex-col pb-4">
                    <div className="text-sm text-gray-500">
                      Last updated on - {formattedReopenedProject?.rawProject?.projectAssigned?.[0]?.assignedDate
                        ? new Date(formattedReopenedProject.rawProject.projectAssigned[0].assignedDate).toLocaleDateString()
                        : new Date().toLocaleDateString()} (
                      {formattedReopenedProject?.rawProject?.projectAssigned?.[0]?.assignedDate
                        ? new Date(formattedReopenedProject.rawProject.projectAssigned[0].assignedDate).toLocaleTimeString()
                        : new Date().toLocaleTimeString()})
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm font-medium">
                        <PiRepeatFill className="w-4 h-4" />
                        <span>Reopened Project</span>
                      </span>
                    </div>
                  </div>

                  {reopenedProjectLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
                    </div>
                  ) : (
                    <div className="flex flex-col py-6">
                      <div className="grid grid-cols-2 gap-6">
                        <InputField
                          label="Company Name"
                          value={formattedReopenedProject?.customerName || 'N/A'}
                          className="bg-gray-100"
                          disabled
                          icon={<CiUser className="w-5 h-5" />}
                        />
                        <InputField
                          label="Article Number"
                          value={formattedReopenedProject?.articleNo || 'N/A'}
                          className="bg-gray-100"
                          disabled
                          icon={<FaRegFileAlt className="w-5 h-5" />}
                        />
                        <InputField
                          label="Project Name"
                          value={formattedReopenedProject?.projectName || 'N/A'}
                          className="bg-gray-100"
                          disabled
                          icon={<FiFileText className="w-5 h-5" />}
                        />
                        <InputField
                          label="Assigned To"
                          value={(() => {
                            if (formattedReopenedProject.rawProject?.assignedEmployeeName) {
                              const isSelfAssigned = formattedReopenedProject.rawProject.isSelfAssigned;
                              return isSelfAssigned
                                ? `${formattedReopenedProject.rawProject.assignedEmployeeName} (Self Assigned)`
                                : formattedReopenedProject.rawProject.assignedEmployeeName;
                            }

                            if (
                              formattedReopenedProject.rawProject?.projectAssigned &&
                              formattedReopenedProject.rawProject.projectAssigned.length > 0
                            ) {
                              const latestAssignment = formattedReopenedProject.rawProject.projectAssigned[0];

                              if (latestAssignment.assignedToName) {
                                return latestAssignment.adminId
                                  ? `${latestAssignment.assignedToName} (Self Assigned)`
                                  : latestAssignment.assignedToName;
                              }
                            }

                            return 'Not specified';
                          })()}
                          className="bg-gray-100"
                          disabled
                          icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                        />
                        <InputField
                          label="Received On"
                          value={new Date(
                            formattedReopenedProject.rawProject?.receivedOn ||
                            formattedReopenedProject.rawProject?.createdAt ||
                            Date.now()
                          ).toLocaleDateString()}
                          className="bg-gray-100"
                          disabled
                          icon={<AiOutlineClockCircle className="w-5 h-5" />}
                        />

                        {/* Warranty fields for reopened project if applicable */}
                        {formattedReopenedProject.status === 'Billed' && formattedReopenedProject.rawProject?.warrantyUntil ? (
                          <>
                            <InputField
                              label="Bill Number"
                              value={formattedReopenedProject.rawProject?.billNumber || 'Not specified'}
                              className="bg-gray-100"
                              disabled
                              icon={<BiReceipt className="w-5 h-5" />}
                            />
                            <div className="relative">
                              <InputField
                                label="Warranty Until"
                                value={new Date(formattedReopenedProject.rawProject.warrantyUntil).toLocaleDateString()}
                                className={`bg-gray-100 ${new Date(formattedReopenedProject.rawProject.warrantyUntil) > new Date()
                                  ? 'border-green-500 text-green-700'
                                  : 'border-red-500 text-red-700'
                                  }`}
                                disabled
                                icon={<AiOutlineClockCircle className="w-5 h-5" />}
                              />
                              <div
                                className={`absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold ${new Date(formattedReopenedProject.rawProject.warrantyUntil) > new Date()
                                  ? 'bg-green-100 text-green-700 border border-green-500'
                                  : 'bg-red-100 text-red-700 border border-red-500'
                                  }`}
                                style={{ margin: 0 }}
                              >
                                {new Date(formattedReopenedProject.rawProject.warrantyUntil) > new Date() ? 'Active' : 'Expired'}
                              </div>
                            </div>
                            <InputField
                              label="Billed Date"
                              value={new Date(
                                formattedReopenedProject.rawProject.projectAssigned.find(
                                  (a: any) => a.status === 'BILLED'
                                )?.assignedDate || formattedReopenedProject.rawProject.projectAssigned[0].assignedDate
                              ).toLocaleDateString()}
                              className="bg-gray-100"
                              disabled
                              icon={<BiReceipt className="w-5 h-5" />}
                            />
                            <div className="col-span-2">
                              <button
                                onClick={() => setShowReopenedAssignmentHistory(!showReopenedAssignmentHistory)}
                                className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                                  flex items-center justify-between
                                  ${showReopenedAssignmentHistory
                                    ? 'bg-blue-50 border-blue-600 text-blue-700'
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                  }`}
                              >
                                <div className="flex items-center gap-2">
                                  <FiClock className="h-5 w-5" />
                                  <span className="font-medium">View History</span>
                                </div>
                                {showReopenedAssignmentHistory ? (
                                  <FiChevronUp className="h-5 w-5" />
                                ) : (
                                  <FiChevronDown className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                          </>
                        ) : (
                          // View History Button (only shown if warranty fields are not displayed)
                          <button
                            onClick={() => setShowReopenedAssignmentHistory(!showReopenedAssignmentHistory)}
                            className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                              flex items-center justify-between
                              ${showReopenedAssignmentHistory
                                ? 'bg-blue-50 border-blue-600 text-blue-700'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            <div className="flex items-center gap-2">
                              <FiClock className="h-5 w-5" />
                              <span className="font-medium">View History</span>
                            </div>
                            {showReopenedAssignmentHistory ? (
                              <FiChevronUp className="h-5 w-5" />
                            ) : (
                              <FiChevronDown className="h-5 w-5" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Assignment History Table */}
                      <div
                        className={`mt-3 transition-all duration-300 ${showReopenedAssignmentHistory ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                          }`}
                      >
                        <TaskAssignmentTable
                          assignments={reopenedAssignmentHistory}
                          visible={showReopenedAssignmentHistory}
                        />
                      </div>

                      {/* Product Attachments */}
                      <div className="py-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Product Attachments</h3>
                        <div className="flex flex-wrap gap-4">
                          {formattedReopenedProject.rawProject?.profilePicture &&
                            formattedReopenedProject.rawProject.profilePicture.trim() !== '' &&
                            !formattedReopenedProject.rawProject.profilePicture.includes('default-project-image.png') ? (
                            <div className="relative">
                              <img
                                src={formattedReopenedProject.rawProject.profilePicture}
                                alt={`Project ${formattedReopenedProject.projectName} Image`}
                                className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => openImageViewer(formattedReopenedProject.rawProject.profilePicture)}
                              />
                            </div>
                          ) : formattedReopenedProject.rawProject?.projectImages?.length > 0 ? (
                            formattedReopenedProject.rawProject.projectImages.map((image: any, index: number) => (
                              <div key={index} className="relative">
                                <img
                                  src={image}
                                  alt={`Project Attachment ${index + 1}`}
                                  className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => openImageViewer(image)}
                                />
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500">No attachments available</p>
                          )}
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="mt-6">
                        <button
                          onClick={() => {
                            projectDetailsPopup.closePopup();
                            handleEditProject(formattedReopenedProject);
                          }}
                          className="w-full px-4 py-4 text-lg font-semibold rounded-lg flex items-center border-2 justify-center gap-2 bg-lime-100 hover:bg-lime-200 border-lime-600 text-lime-700"
                          title="Edit reopened project"
                        >
                          <TbEdit className="mr-1" /> Edit Project
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* End of Dual View Container */}
          </div>
        </PopupWrapper>
      )}

      {/* Image Viewer */}
      <ImageViewer isOpen={isImageViewerOpen} imageUrl={viewerImage} onClose={closeImageViewer} />
    </React.Fragment>
  );
};

export default MyProjects;
